import React, { useLayoutEffect, useRef } from "react";
import "../../orion/styles/orion.css";
import "./page-header.css";

/**
 * The page header — the dashboard's own hero band, not a copy of it.
 *
 * It renders Orion's `orion-plate orion-hero` markup and classes directly, so
 * the card, the badge, the hairline rule and the display title are the same
 * ones dashboard4 draws and stay in step with it. The palette comes from
 * `orion-tokens`, which carries Orion's variables without its page shell (see
 * the split in orion.css), so a page can wear the hero without becoming Orion.
 *
 * Layout, per the order these pages want: the section's mark, the rule, the
 * title, then what the page acts on, the tab strip through the middle, and
 * anything else at the right edge.
 *
 * The title can carry the dashboard's own mono eyebrow under it, by way of
 * `eyebrow` - one line for a string, or a line each for an array, which is how
 * a page puts one counter per line. A page that passes none keeps the single
 * line it always had.
 *
 * @param mark        the section's icon element (the rail's own NavX marks)
 * @param markTitle   what the badge says on hover - a page that carries state
 *                    in its mark (Scan's orchestrator) puts it here
 * @param onMarkClick optional handler on the badge
 * @param title       one line of text
 * @param eyebrow     what goes under it, in the dashboard's mono eyebrow: one
 *                    line, or an array of them - a counter to a line
 * @param add         a page's add control, placed out of flow to the left of
 *                    the refresh so its presence never moves the refresh
 * @param actions     what follows the title — the refresh
 * @param menu        the tab strip, through the middle
 * @param trailing    anything pinned to the right edge
 * @param loading     while true, the dashboard's two fetch cues run: the arc
 *                    sweeping the badge and the bar crossing the band's top
 */
/**
 * The tab strip centres in the room it has, and shrinks to fit when there is not
 * enough of it.
 *
 * The floor is CSS's (`justify-content: safe center`); the scale is not, because
 * it needs the strip's natural width measured against the box, which no CSS
 * length can express. A transform rather than flex shrinking: shrinking squeezes
 * the buttons and runs the labels into each other, where scaling takes the type
 * down with the box and the strip keeps its proportions.
 */
function useFitMenu(boxRef, rootRef) {
  useLayoutEffect(() => {
    const box = boxRef.current;
    const strip = box?.firstElementChild;
    const root = rootRef.current;
    if (!box || !strip || !root) return undefined;

    const toolbar = box.closest(".page-header-toolbar");

    const fit = () => {
      // offsetWidth is the layout width, which a transform does not change - so
      // this stays the strip's natural width however often we rescale it.
      const natural = strip.offsetWidth;
      if (!natural) return;

      // The box is sized for the table's three icon buttons. Opening the search
      // puts a field in front of them, so the room is measured from whatever is
      // actually leftmost over there rather than from the box alone.
      const boxBox = box.getBoundingClientRect();
      const rightmost = toolbar?.querySelector(
        ".MuiCollapse-horizontal, [aria-label='Show/Hide search']"
      );
      const limit = rightmost
        ? Math.min(boxBox.right, rightmost.getBoundingClientRect().left - 12)
        : boxBox.right;
      // The box starts at a fixed offset, sized for a page carrying one control
      // beside its title. A page with more of them - Scan has five - would have
      // its strip painted over them, so the floor is whichever is further right.
      const actions = root.querySelector(".page-header__actions");
      const floor = actions
        ? Math.max(boxBox.left, actions.getBoundingClientRect().right + 12)
        : boxBox.left;
      const available = Math.max(0, limit - floor);
      if (!available) return;

      // Floored: with the search open there is genuinely not enough room for a
      // five-tab strip, and scaling all the way down turns the labels into
      // specks. It stops at three quarters and the field covers what is left.
      const scale = natural > available ? Math.max(0.75, available / natural) : 1;
      // on the root, not the strip: the header's own buttons read it too, so
      // they come down with the tabs rather than towering over them
      root.style.setProperty("--page-header-menu-scale", scale.toFixed(4));

      // Centred on the whole band, not on the room it was given: the box starts
      // after the header's controls, so centring inside it would sit the strip
      // well right of the band's middle. The offset is what carries it back to
      // the middle, and it never goes below zero - that is the floor.
      const band = box.offsetParent;
      if (!band) return;
      const bandBox = band.getBoundingClientRect();
      const painted = natural * scale;
      const centred = bandBox.left + bandBox.width / 2 - painted / 2;
      const offset = Math.max(floor - boxBox.left, centred - boxBox.left);
      root.style.setProperty("--page-header-menu-offset", `${offset.toFixed(1)}px`);
    };

    fit();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(fit);
      observer.observe(box);
      observer.observe(strip);
      if (toolbar) observer.observe(toolbar);
    }
    // the search field mounts and unmounts rather than resizing, so the toolbar
    // is watched for that too
    let mutations;
    if (toolbar && typeof MutationObserver !== "undefined") {
      mutations = new MutationObserver(fit);
      mutations.observe(toolbar, { childList: true, subtree: true });
    }
    window.addEventListener("resize", fit);

    return () => {
      observer?.disconnect();
      mutations?.disconnect();
      window.removeEventListener("resize", fit);
    };
  });
}

/**
 * A counter line, split so the lines under each other line up: the figure into
 * a column of its own, the words after it. Without this "0 hosts" and
 * "16 links" start their words two characters apart, which reads as a ragged
 * edge rather than as a list.
 *
 * Split at the first space, so a line that carries two counters
 * ("16 links · 297 apps") aligns on its first figure like the rest. Anything
 * that is not a plain string - a node a page built itself - is left alone.
 */
function countParts(line) {
  if (typeof line !== "string") return line;
  const at = line.indexOf(" ");
  if (at < 1) return line;
  return (
    <>
      <b className="orion-eyebrow__n">{line.slice(0, at)}</b>
      <span className="orion-eyebrow__t">{line.slice(at + 1)}</span>
    </>
  );
}

export default function PageHeader({
  mark,
  markTitle,
  onMarkClick,
  title,
  eyebrow,
  add,
  actions,
  menu,
  trailing,
  loading = false,
  className = "",
}) {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  useFitMenu(menuRef, rootRef);

  return (
    <div className={`orion-tokens page-header ${className}`} ref={rootRef}>
      <section className="orion-plate orion-hero">
        {loading && <span className="orion-refreshbar" aria-hidden="true" />}

        <span
          className={`orion-hero__badge${loading ? " is-loading" : ""}`}
          title={markTitle}
          onClick={onMarkClick}
        >
          {/* the ring shows only while fetching; the arc is what spins */}
          <svg className="orion-hero__ring" viewBox="0 0 40 40" aria-hidden="true">
            <circle cx="20" cy="20" r="18" fill="none" stroke="rgba(var(--ink), 0.12)" strokeWidth="1.5" />
            <circle
              className="orion-hero__ring-arc"
              cx="20"
              cy="20"
              r="18"
              fill="none"
              stroke="var(--accent)"
              strokeWidth="1.5"
              strokeLinecap="round"
              pathLength="100"
              strokeDasharray="26 74"
            />
          </svg>
          {mark && React.cloneElement(mark, {
            className: "orion-hero__mark",
            width: 24,
            height: 24,
            "aria-hidden": "true",
          })}
        </span>

        <div className="orion-hero__rule" />

        <div className="orion-hero__text">
          <span className="orion-display orion-hero__title">{title}</span>
          {/* the hero's own class, so the type, the tracking and the colour
              come from one place rather than being matched by eye on every
              page that wants them. An array is a line each: a counter reads
              faster stacked than run together behind separators. */}
          {(Array.isArray(eyebrow) ? eyebrow : [eyebrow]).filter(Boolean).length > 0 && (
            /* The lines share one grid, which is what lines their words up: a
               column each for the figure and the words, sized to the widest
               figure in this header rather than to a guess. The line itself is
               `display: contents`, so its two halves are the grid's own items
               while the type and colour still come from its class. */
            <div className="orion-eyebrows">
              {(Array.isArray(eyebrow) ? eyebrow : [eyebrow])
                .filter(Boolean)
                .map((line, i) => (
                  <span className="orion-eyebrow" key={typeof line === "string" ? line : i}>
                    {countParts(line)}
                  </span>
                ))}
            </div>
          )}
        </div>

        {(add || actions) && (
          <span className="page-header__actions">
            {add && <span className="page-header__add">{add}</span>}
            {actions}
          </span>
        )}

        {menu && <div className="page-header__menu" ref={menuRef}>{menu}</div>}

        {trailing && <div className="orion-hero__actions">{trailing}</div>}
      </section>
    </div>
  );
}
